'use client';

import '../../../app/globals.css'

import { getCsrfToken, getProviders, signIn, useSession } from 'next-auth/react';
import AuthError from 'next-auth';
import { useCallback, useEffect, useState, useTransition } from "react";
import { toast } from "react-hot-toast";
import { FieldValues, SubmitHandler, useForm } from "react-hook-form";
import { FcGoogle } from "react-icons/fc";
import { FaFacebook, FaEye, FaEyeSlash } from "react-icons/fa";
import { useRouter, useSearchParams } from "next/navigation";
import { GetServerSidePropsContext } from 'next';

import useRegisterModal from "@/app/hooks/useRegisterModal";
import useLoginModal from "@/app/hooks/useLoginModal";

import Modal from "./Modal";
// import Input from "../inputs/Input";
import Heading from "../Heading";
import Button from "../Button";
import { SafeUser, SimplifiedUser } from '@/app/types';
import { BiError } from 'react-icons/bi';
import * as z from "zod";
import { LoginSchema } from '@/app/schemas';
import { zodResolver } from '@hookform/resolvers/zod';
import { ButtonS } from "@/components/ui/button"
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import { FormError } from '@/app/components/FormError';
import { FormSuccess } from '@/app/components/FormSucess';
import { login } from "@/app/actions/login";
// import { cookies } from 'next/headers';

interface LoginSelectProps {
  currentUser?: SimplifiedUser | null;
  // csrfToken?: string | undefined;
}

const LoginModal: React.FC<LoginSelectProps> = ({
  currentUser,
  // csrfToken,
}) => {
  const router = useRouter();
  const [isPending, startTransition] = useTransition();
  const loginModal = useLoginModal();
  const registerModal = useRegisterModal();
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | undefined>("");
  const [success, setSuccess] = useState<string | undefined>("");

  const searchParams = useSearchParams();
  const urlError = searchParams?.get("error") === "OAuthAccountNotLinked"
    ? "Email already in use with a different provider!"
    : "";

  useEffect(() => {
    if (urlError) {
      loginModal.onOpen();
    }
  }, [urlError]);

  const form = useForm<z.infer<typeof LoginSchema>>({
    resolver: zodResolver(LoginSchema),
    defaultValues: {
      email: "",
      password: "",
    }
  })

  const onSubmit = (values: z.infer<typeof LoginSchema>) => {
    setError("");
    setSuccess("");

    startTransition(() => {
      login(values)
        .then((data) => {
          // setError(data.error);
          // setSuccess(data.success);

          if (data.error) {
            setError(data.error);
            return;
          }

          if (data.success) {
            form.reset();
            setSuccess(data.success);
          }

          // if (!csrfToken) {
          //   toast.error('Error: CSRF token is missing');
          //   setError(data.error);
          //   setIsLoading(false);
          //   return;
          // }

          try {
            signIn('credentials', {
              ...values,
              redirect: false,
              // redirect: true,
              // callbackUrl: '/',
              // csrfToken: csrfToken
            })
              .then((callback) => {
                setIsLoading(false);

                if (callback?.ok) {
                  toast.success('Logged in');
                  router.refresh();
                  loginModal.onClose();
                }

              });
          } catch (error) {
            if (error instanceof AuthError) {
              switch (error) {
                case "CredentialsSignin":
                  return { error: "Invalid credentials!" };
                default:
                  return { error: "Something went wrong!" };
              }
            }

            throw error;
          }

        })
        .catch(() => setError("Something went wrong"));
    });
  };

  const onToggle = useCallback(() => {
    loginModal.onClose();
    registerModal.onOpen();
  }, [loginModal, registerModal])

  const onToggleRegister = useCallback(() => {
    loginModal.onClose();
    router.push("/registercenter")
  }, [loginModal])


  const bodyContent = (
    <div className="flex flex-col gap-4">
      <Heading
        title="Welcome back"
        subtitle="Login to your account!"
      />
      <div className="mb-4">
        <Button
          outline
          label="Continue with Google"
          icon={FcGoogle}
          onClick={() => signIn('google', { callbackUrl: window.location.href })}
        />
      </div>
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onSubmit)}
          className="space-y-6"
        >
          <div className="space-y-4">
            <>
              <FormField
                control={form.control}
                name="email"
                render={({ field }: any) => (
                  <FormItem>
                    <FormLabel>Email</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        disabled={isPending}
                        placeholder="example@example.com"
                        type="email"
                      />
                    </FormControl>
                    <FormMessage className="text-red-500" />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="password"
                render={({ field }: any) => (
                  <FormItem>
                    <FormLabel>Password</FormLabel>
                    <div className="relative w-full">
                      <FormControl>
                        <Input
                          {...field}
                          disabled={isPending}
                          placeholder="******"
                          type={showPassword ? "text" : "password"}
                          className="pr-10"
                        />
                      </FormControl>
                      <button
                        type="button"
                        onClick={() => setShowPassword((prev) => !prev)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none"
                      >
                        {showPassword ? <FaEyeSlash size={18} /> : <FaEye size={18} />}
                      </button>
                    </div>
                    <FormMessage className="text-red-500" />
                  </FormItem>
                )}
              />
            </>
          </div>
          <FormError message={error || urlError} />
          <FormSuccess message={success} />
          <ButtonS
            type="submit"
            disabled={isPending}
            className="h-10 disabled:opacity-70 disabled:cursor-not-allowed rounded-lg hover:opacity-80 transition w-full bg-cyan-600 font-semibold text-white text-md py-3"
          >
            Login
          </ButtonS>
        </form>
      </Form>
      <div className="flex justify-end mt-2 font-bold">
        <span
          onClick={() => {
            loginModal.onClose();
            router.push("/auth/forgotpassword");
          }}
          className="text-cyan-700 cursor-pointer hover:underline"
        >
          Forgot password?
        </span>
      </div>
    </div>
  )

  const footerContent = (
    <div className="flex flex-col gap-4 mt-3">
      <div className="text-neutral-500 text-center font-light">
        <p>First time here?
          <span
            onClick={onToggle}
            className="text-neutral-800 cursor-pointer hover:underline"
          > Create an account as a DIVER</span>
        </p>
        <p>
          <span
            onClick={onToggleRegister}
            className="text-cyan-700 cursor-pointer hover:underline"
          > Create an account as a DIVE CENTER</span>
        </p>
      </div>
    </div>
  )

  return (
    <Modal
      disabled={isLoading}
      isOpen={loginModal.isOpen}
      title="Login"
      // actionLabel="Continue"
      onClose={loginModal.onClose}
      // onSubmit={handleSubmit(onSubmit)}
      body={bodyContent}
      footer={footerContent}
    />
  );
}

export default LoginModal;


